import { getMemberColor } from './colorUtils';

/**
 * Escapes HTML characters to prevent XSS while allowing structured markdown transformation.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Custom Markdown to HTML converter tailored for Alfred Executive Summaries.
 * Supports:
 * - GitHub-style Alerts (> [!NOTE], > [!IMPORTANT], > [!WARNING], > [!TIP], > [!CAUTION])
 * - Member Mentions ([[@Nome]]) with HSL colored badges
 * - Headings (h1, h2, h3)
 * - Lists & Task Checkboxes (- [x], - [ ])
 * - Tables, Blockquotes, Bold, Italic, Code Inline
 */
export function renderMarkdownToHtml(markdown: string): string {
  if (!markdown || typeof markdown !== 'string') return '';

  const lines = markdown.split('\n');
  const resultLines: string[] = [];
  
  let inList = false;
  let listType: 'ul' | 'ol' = 'ul';
  let inBlockquote = false;
  let alertType: 'NOTE' | 'IMPORTANT' | 'WARNING' | 'TIP' | 'CAUTION' | null = null;
  let alertLines: string[] = [];
  let inTable = false;
  let tableHeaders: string[] = [];
  let tableRows: string[][] = [];

  const closeList = () => {
    if (inList) {
      resultLines.push(listType === 'ul' ? '</ul>' : '</ol>');
      inList = false;
    }
  };

  const closeAlert = () => {
    if (inBlockquote && alertType) {
      const alertConfigs = {
        NOTE: {
          bg: 'bg-blue-950/40',
          border: 'border-blue-500/50',
          titleColor: 'text-blue-300',
          badgeBg: 'bg-blue-500/20 text-blue-300 border-blue-400/30',
          icon: 'ℹ️',
          label: 'Nota'
        },
        IMPORTANT: {
          bg: 'bg-purple-950/40',
          border: 'border-purple-500/50',
          titleColor: 'text-purple-300',
          badgeBg: 'bg-purple-500/20 text-purple-300 border-purple-400/30',
          icon: '📌',
          label: 'Importante'
        },
        WARNING: {
          bg: 'bg-amber-950/40',
          border: 'border-amber-500/50',
          titleColor: 'text-amber-300',
          badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
          icon: '⚠️',
          label: 'Atenção'
        },
        TIP: {
          bg: 'bg-emerald-950/40',
          border: 'border-emerald-500/50',
          titleColor: 'text-emerald-300',
          badgeBg: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
          icon: '💡',
          label: 'Dica'
        },
        CAUTION: {
          bg: 'bg-rose-950/40',
          border: 'border-rose-500/50',
          titleColor: 'text-rose-300',
          badgeBg: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
          icon: '🚨',
          label: 'Risco'
        }
      };

      const cfg = alertConfigs[alertType];
      const bodyHtml = alertLines.map(l => processInlineMarkdown(l)).join('<br/>');

      resultLines.push(`
        <div class="my-3 p-3.5 rounded-xl ${cfg.bg} border ${cfg.border} shadow-md">
          <div class="flex items-center gap-2 mb-1.5">
            <span class="px-2 py-0.5 rounded-md text-[10px] font-extrabold ${cfg.badgeBg} border uppercase tracking-wider flex items-center gap-1">
              <span>${cfg.icon}</span> ${cfg.label}
            </span>
          </div>
          <div class="text-xs ${cfg.titleColor} leading-relaxed font-medium">
            ${bodyHtml}
          </div>
        </div>
      `);

      alertType = null;
      alertLines = [];
      inBlockquote = false;
    } else if (inBlockquote) {
      const bodyHtml = alertLines.map(l => processInlineMarkdown(l)).join('<br/>');
      resultLines.push(`
        <blockquote class="my-3 pl-3.5 py-1 border-l-2 border-cyan-400/70 text-slate-300 text-xs italic bg-slate-900/40 rounded-r-lg">
          ${bodyHtml}
        </blockquote>
      `);
      alertLines = [];
      inBlockquote = false;
    }
  };

  const closeTable = () => {
    if (inTable) {
      let headerHtml = '';
      if (tableHeaders.length > 0) {
        headerHtml = `<thead><tr class="bg-slate-900 border-b border-slate-800">${tableHeaders.map(h => `<th class="px-3 py-2 text-left text-[11px] font-bold text-cyan-300 uppercase tracking-wider">${processInlineMarkdown(h)}</th>`).join('')}</tr></thead>`;
      }
      let rowsHtml = '';
      if (tableRows.length > 0) {
        rowsHtml = `<tbody>${tableRows.map((r, idx) => `<tr class="${idx % 2 === 0 ? 'bg-slate-950/40' : 'bg-slate-900/40'} border-b border-slate-800/60">${r.map(cell => `<td class="px-3 py-2 text-xs text-slate-200">${processInlineMarkdown(cell)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      }
      resultLines.push(`<div class="my-3 overflow-x-auto rounded-xl border border-slate-800"><table class="w-full text-left border-collapse">${headerHtml}${rowsHtml}</table></div>`);
      inTable = false;
      tableHeaders = [];
      tableRows = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmedLine = rawLine.trim();

    if (!trimmedLine) {
      closeList();
      closeAlert();
      closeTable();
      continue;
    }

    // GitHub Alert Header: > [!NOTE], > [!IMPORTANT], > [!WARNING], > [!TIP], > [!CAUTION]
    const alertMatch = trimmedLine.match(/^>\s*\[\!(NOTE|IMPORTANT|WARNING|TIP|CAUTION)\]/i);
    if (alertMatch) {
      closeList();
      closeTable();
      closeAlert();
      inBlockquote = true;
      alertType = alertMatch[1].toUpperCase() as any;
      continue;
    }

    // Standard Blockquote Line: > Text
    if (trimmedLine.startsWith('>')) {
      closeList();
      closeTable();
      if (!inBlockquote) {
        inBlockquote = true;
      }
      const quoteText = trimmedLine.replace(/^>\s?/, '');
      alertLines.push(quoteText);
      continue;
    } else if (inBlockquote) {
      closeAlert();
    }

    // GFM Table Row: | Header 1 | Header 2 |
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      closeList();
      closeAlert();
      const cells = trimmedLine.split('|').slice(1, -1).map(c => c.trim());
      
      // Separator row: |---|---|
      if (cells.every(c => /^:?-+:?$/.test(c))) {
        continue;
      }

      if (!inTable) {
        inTable = true;
        tableHeaders = cells;
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      closeTable();
    }

    // Headings: # H1, ## H2, ### H3
    if (trimmedLine.startsWith('#')) {
      closeList();
      closeAlert();
      closeTable();

      const hMatch = trimmedLine.match(/^(#{1,3})\s+(.*)$/);
      if (hMatch) {
        const level = hMatch[1].length;
        const text = processInlineMarkdown(hMatch[2]);
        if (level === 1) {
          resultLines.push(`<h2 class="text-sm font-extrabold text-cyan-300 border-b border-slate-800/80 pb-1.5 mt-5 mb-3 flex items-center gap-2 tracking-wide"><span class="text-cyan-400">#</span> ${text}</h2>`);
        } else if (level === 2) {
          resultLines.push(`<h3 class="text-xs font-bold text-slate-100 border-b border-slate-800/60 pb-1 mt-4 mb-2.5 flex items-center gap-2"><span class="text-cyan-400">##</span> ${text}</h3>`);
        } else {
          resultLines.push(`<h4 class="text-[11px] font-bold text-slate-200 mt-3 mb-2 flex items-center gap-1.5"><span class="text-cyan-400">###</span> ${text}</h4>`);
        }
        continue;
      }
    }

    // Task Checkboxes: - [x] or - [ ]
    const taskMatch = trimmedLine.match(/^[-*]\s+\[([ xX])\]\s+(.*)$/);
    if (taskMatch) {
      closeAlert();
      closeTable();
      if (!inList || listType !== 'ul') {
        closeList();
        inList = true;
        listType = 'ul';
        resultLines.push('<ul class="space-y-2 my-2 text-xs">');
      }
      const isChecked = taskMatch[1].toLowerCase() === 'x';
      const text = processInlineMarkdown(taskMatch[2]);
      resultLines.push(`
        <li class="flex items-start gap-2 bg-slate-950/50 p-2 rounded-lg border border-slate-800/60">
          <span class="mt-0.5 flex-shrink-0 text-xs">${isChecked ? '✅' : '⬜'}</span>
          <span class="${isChecked ? 'line-through text-slate-400' : 'text-slate-200 font-medium'} leading-relaxed">${text}</span>
        </li>
      `);
      continue;
    }

    // Unordered List Items: - Item or * Item
    const ulMatch = trimmedLine.match(/^[-*]\s+(.*)$/);
    if (ulMatch) {
      closeAlert();
      closeTable();
      if (!inList || listType !== 'ul') {
        closeList();
        inList = true;
        listType = 'ul';
        resultLines.push('<ul class="space-y-1.5 my-2 text-xs text-slate-200">');
      }
      const text = processInlineMarkdown(ulMatch[1]);
      resultLines.push(`
        <li class="flex items-start gap-2">
          <span class="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></span>
          <span class="leading-relaxed">${text}</span>
        </li>
      `);
      continue;
    }

    // Ordered List Items: 1. Item
    const olMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
    if (olMatch) {
      closeAlert();
      closeTable();
      if (!inList || listType !== 'ol') {
        closeList();
        inList = true;
        listType = 'ol';
        resultLines.push('<ol class="space-y-1.5 my-2 text-xs text-slate-200">');
      }
      const num = olMatch[1];
      const text = processInlineMarkdown(olMatch[2]);
      resultLines.push(`
        <li class="flex items-start gap-2">
          <span class="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-800 text-cyan-300 border border-slate-700 flex-shrink-0">${num}.</span>
          <span class="leading-relaxed mt-0.5">${text}</span>
        </li>
      `);
      continue;
    }

    // Regular Paragraph Line
    closeList();
    closeAlert();
    closeTable();
    resultLines.push(`<p class="my-2 text-xs text-slate-200 leading-relaxed font-normal">${processInlineMarkdown(trimmedLine)}</p>`);
  }

  closeList();
  closeAlert();
  closeTable();

  return resultLines.join('\n');
}

/**
 * Process inline Markdown rules (Bold, Italic, Code, Mentions [[@Name]], URLs).
 */
export function processInlineMarkdown(text: string): string {
  if (!text) return '';

  let escaped = escapeHtml(text);

  // 1. Mentions: [[@Nome do Participante]] or [[@Todos]]
  escaped = escaped.replace(/\[\[@([\s\S]+?)\]\]/g, (match, name) => {
    const cleanName = name.trim();
    const isTodos = cleanName.toLowerCase() === 'todos' || cleanName.toLowerCase() === 'all';
    if (isTodos) {
      return `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 rounded-md text-[11px] font-extrabold bg-amber-500/25 text-amber-300 border border-amber-400/40 shadow-xs">@Todos</span>`;
    }
    const color = getMemberColor(cleanName);
    return `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 rounded-md text-[11px] font-bold ${color.bg} ${color.text} ${color.border} border shadow-xs">@${cleanName}</span>`;
  });

  // 2. Bold: **text**
  escaped = escaped.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-slate-50">$1</strong>');

  // 3. Italic: *text* or _text_
  escaped = escaped.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em class="italic text-slate-200">$1</em>');
  escaped = escaped.replace(/_(.*?)_/g, '<em class="italic text-slate-200">$1</em>');

  // 4. Inline Code: `code`
  escaped = escaped.replace(/`(.*?)`/g, '<code class="px-1.5 py-0.5 rounded bg-slate-900 text-cyan-300 border border-slate-800 font-mono text-[11px]">$1</code>');

  return escaped;
}
